import { createContext, useState, useCallback, useRef } from "react";

// deccaState string enum:
//   'closed'     — niet zichtbaar
//   'sidebar'    — rechts vastgezet, normale breedte
//   'fullscreen' — schermvullend
//   'embedded'   — gemounted binnen een andere container (bv. TOC fullscreen view)
//
// `chatOpen` + `setChatOpen` blijven beschikbaar als compat-laag voor 13+ bestaande callsites.
//   chatOpen          → afgeleid: deccaState !== 'closed'
//   setChatOpen(true) → opent in laatste niet-'closed' stand (default: 'sidebar')
//   setChatOpen(false)→ sluit
//
// localStorage: laatste open-stand (sidebar | fullscreen) wordt onthouden zodat
// de gebruiker bij heropenen dezelfde stand terugkrijgt. 'closed' en 'embedded' niet persisted.

const STORAGE_KEY = "decca:lastOpenState";

const readLastOpenState = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "fullscreen" || stored === "sidebar" ? stored : "sidebar";
  } catch {
    return "sidebar";
  }
};

export const DeccaContext = createContext();

export const DeccaProvider = ({ children }) => {
  const [deccaState, setDeccaStateRaw] = useState("closed");
  const lastOpenStateRef = useRef(readLastOpenState());

  const [selectedConversationID, setSelectedConversationID] = useState(null);
  const [showConversations, setShowConversations] = useState(true);
  const [subject, setSubject] = useState("");
  const [contextData, setContextData] = useState();
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedEffect, setSelectedEffect] = useState(null);
  // entryPoint records how the chat was opened so the router can treat page
  // context as a hint, not a lock: 'topbar' (page-agnostic start) vs 'button'
  // (opened on a specific entity). Null when closed / not yet set.
  const [entryPoint, setEntryPoint] = useState(/** @type {string | null} */ (null));
  // pendingAction holds a write the orchestrator proposed (confirmation card),
  // awaiting the user's approve/cancel before the server executes it.
  const [pendingAction, setPendingAction] = useState(/** @type {object | null} */ (null));
  // tenantOverride lets a Decca conversation analyze a tenant other than the logged-in one
  // (e.g. a portfolio manager analyzing a viewed project's tenant). Null = use logged-in client.
  const [tenantOverride, setTenantOverride] = useState(null);
  const [tenantNameOverride, setTenantNameOverride] = useState(null);
  // tocContext tells Decca where in the Theory of Change navigator the user is
  // (current step / node / overview-vs-detail). Transient, like tenantOverride:
  // it is only meaningful while the user is on the TOC page, so it auto-clears
  // on close to avoid leaking into a conversation opened elsewhere.
  // Shape: { step: 'doel'|'act'|'mech'|'eff', nodeId: string|null, path: string[], mode: 'detail'|'overview' }
  const [tocContext, setTocContext] = useState(/** @type {object | null} */ (null));

  // Wrap chatOpen setter so closing the sidebar always clears transient context
  // (tenant override + TOC position). Prevents leakage into the next conversation
  // triggered by a different page (e.g. analysis components that don't manage
  // this state explicitly).
  const setChatOpenSafe = (open) => {
    setChatOpen(open);
    if (!open) {
      setTenantOverride(null);
      setTenantNameOverride(null);
      setTocContext(null);
    }
  };

  const setDeccaState = useCallback((newState) => {
    if (newState === "sidebar" || newState === "fullscreen") {
      try {
        localStorage.setItem(STORAGE_KEY, newState);
      } catch {}
      lastOpenStateRef.current = newState;
    }
    setDeccaStateRaw(newState);
  }, []);

  const openDecca = useCallback(() => {
    setDeccaStateRaw(lastOpenStateRef.current);
  }, []);

  const chatOpen = deccaState !== "closed";

  const setChatOpen = useCallback(
    (open) => {
      if (open) {
        setDeccaStateRaw((prev) => (prev === "closed" ? lastOpenStateRef.current : prev));
      } else {
        setDeccaStateRaw("closed");
      }
    },
    []
  );

  return (
    <DeccaContext.Provider
      value={{
        deccaState,
        setDeccaState,
        openDecca,
        chatOpen,
        setChatOpen: setChatOpenSafe,
        selectedConversationID,
        setSelectedConversationID,
        showConversations,
        setShowConversations,
        subject,
        setSubject,
        contextData,
        setContextData,
        selectedActivity,
        setSelectedActivity,
        selectedEffect,
        setSelectedEffect,
        entryPoint,
        setEntryPoint,
        pendingAction,
        setPendingAction,
        tenantOverride,
        setTenantOverride,
        tenantNameOverride,
        setTenantNameOverride,
        tocContext,
        setTocContext,
      }}
    >
      {children}
    </DeccaContext.Provider>
  );
};
